import React, { useEffect, useState } from 'react';
import {
  TextField,
  IconButton,
  Box,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import { CustomTableCell } from 'src/components/MainPage/_shared/Table';
import { activeStyles, disabledStyles } from './editableCellAppearance';

type EditableNumberCellProps = {
  value: number | null;
  onSave: (value: number | null) => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  isEditing: boolean;
  disabled?: boolean;
  suffix?: string;
  isPercentage?: boolean;
};

export const EditableNumberCell = ({
  value,
  onSave,
  onStartEditing,
  onStopEditing,
  isEditing,
  disabled,
  suffix,
  isPercentage = false,
}: EditableNumberCellProps) => {
  const [draft, setDraft] = useState<string>(value?.toString() ?? '');

  useEffect(() => {
    setDraft(value?.toString() ?? '');
  }, [value]);

  const handleChange = (input: string) => {
    const val = input.replace(',', '.');
    if (!/^\d*\.?\d*$/.test(val)) return;
    if (isPercentage && val !== '' && val !== '.') {
      const num = Number(val);
      if (num < 0 || num > 100) return;
    }
    setDraft(val);
  };

  const formatNumber = (num: number | null | undefined, suffix?: string) => {
    if (num === null || num === undefined || isNaN(num)) return '—';
    const formatted = Number(num.toFixed(4)).toString();
    return suffix ? `${formatted}${suffix}` : formatted;
  };

  const handleSave = () => {
    const normalized = draft.replace(',', '.');
    const num = normalized === '' ? null : Number(normalized);
    if (num !== null && isNaN(num)) return;
    if (isPercentage && num !== null) {
      if (num < 0 || num > 100) return;
    }
    onSave(num);
    onStopEditing();
  };

  const handleCancel = () => {
    setDraft(value?.toString() ?? '');
    onStopEditing();
  };

  if (!isEditing) {
    return (
      <Tooltip title="Редактировать">
        <CustomTableCell
          className="number-cell"
          onClick={() => {
            if (!disabled) {
              setDraft(value?.toString() ?? '');
              onStartEditing();
            }
          }}
          sx={disabled ? disabledStyles : activeStyles}
        >
          {value !== null && value !== undefined
            ? formatNumber(value, suffix)
            : '—'}
        </CustomTableCell>
      </Tooltip>
    );
  }

  return (
    <CustomTableCell sx={{ p: 0 }} className="number-cell">
      <Box display={'flex'} gap={0.25}>
        <TextField
          fullWidth
          autoFocus
          size="small"
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          InputProps={
            isPercentage
              ? {
                  endAdornment: <Typography variant="caption">%</Typography>,
                }
              : undefined
          }
          sx={{
            width: 'auto',
            flex: 1,
          }}
        />
        <Stack>
          <Tooltip title="Применить" placement="top">
            <IconButton
              disableRipple
              size="small"
              color="primary"
              onClick={handleSave}
              sx={{ p: 0 }}
            >
              <CheckBoxIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Отменить" placement="bottom">
            <IconButton
              disableRipple
              size="small"
              onClick={handleCancel}
              sx={{ p: 0 }}
            >
              <DisabledByDefaultIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </CustomTableCell>
  );
};
